import React from 'react';
import { Link } from 'react-router-dom';
import { Phone, Calendar, MapPin, Wrench, FileText, CreditCard, CheckCircle2 } from 'lucide-react';
import { useScrollAnimations } from '../hooks/useScrollAnimations';

export const Process = () => {
  useScrollAnimations();
  const steps = [
    {
      icon: <Phone size={36} />,
      number: '01',
      title: 'Job Raised',
      description: 'Contact us by phone, email, or through your letting agent. Provide property details and the issue you\'re experiencing.',
      details: [
        'Phone: 07710 613783',
        'Email: Joe@RoyalSecurity.info',
        'Emergency call-out available'
      ]
    },
    {
      icon: <Calendar size={36} />,
      number: '02',
      title: 'Booking Confirmation',
      description: 'Receive immediate booking confirmation with appointment date, time window, and contact details.',
      details: [
        'Confirmation sent within 1 hour',
        'Clear appointment window',
        'Direct contact number provided'
      ]
    },
    {
      icon: <MapPin size={36} />,
      number: '03',
      title: 'On the Way',
      description: 'Receive ETA updates and on-the-way notification. Direct tenant communication if needed.',
      details: [
        'Real-time ETA updates',
        'Tenant liaison handled',
        'Access arrangements confirmed'
      ]
    },
    {
      icon: <Wrench size={36} />,
      number: '04',
      title: 'First Visit & Assessment',
      description: 'Full diagnosis of the issue with photos. Clear explanation of findings and repair options.',
      details: [
        'Complete diagnosis with photos',
        'Repair vs replace advice',
        'Clear cost breakdown if needed'
      ]
    },
    {
      icon: <FileText size={36} />,
      number: '05',
      title: 'Quote (If Required)',
      description: 'If work beyond initial visit is needed, receive a clear written quote before proceeding.',
      details: [
        'No surprise costs',
        'Itemized breakdown',
        'Timeline for completion'
      ]
    },
    {
      icon: <CheckCircle2 size={36} />,
      number: '06',
      title: 'Work Completion',
      description: 'Quality repairs completed with professional completion report and before/after photos.',
      details: [
        'Guaranteed workmanship',
        'Photo documentation',
        'Completion certificate'
      ]
    },
    {
      icon: <CreditCard size={36} />,
      number: '07',
      title: 'Invoicing & Payment',
      description: 'Clear, itemized invoice. Payment options available. Ongoing support for any follow-ups.',
      details: [
        'Transparent pricing',
        'Multiple payment methods',
        'Follow-up support included'
      ]
    }
  ];

  return (
    <div>
      {/* Hero */}
      <section style={{
        background: 'linear-gradient(135deg, var(--royal-navy) 0%, #222222 100%)',
        padding: '80px 0 60px',
        color: 'var(--royal-white)'
      }}>
        <div className="royal-container">
          <h1 className="royal-heading-xl" style={{ color: 'var(--royal-white)', marginBottom: '16px' }}>
            How It Works
          </h1>
          <p style={{ fontSize: '1.25rem', maxWidth: '700px', color: 'rgba(255, 255, 255, 0.9)' }}>
            A clear, calm, reassuring process from first contact to completion. No surprises, no hassle.
          </p>
        </div>
      </section>

      {/* Process Steps */}
      <section className="royal-section" style={{ background: 'var(--royal-white)' }}>
        <div className="royal-container">
          <div style={{ maxWidth: '900px', margin: '0 auto' }}>
            {steps.map((step, index) => (
              <div key={index} style={{
                display: 'grid',
                gridTemplateColumns: 'auto 1fr',
                gap: '32px',
                marginBottom: index < steps.length - 1 ? '48px' : '0',
                position: 'relative'
              }}>
                {/* Connector Line */}
                {index < steps.length - 1 && (
                  <div style={{
                    position: 'absolute',
                    left: '45px',
                    top: '100px',
                    bottom: '-48px',
                    width: '2px',
                    background: 'var(--border-color)'
                  }}></div>
                )}

                {/* Icon & Number */}
                <div style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: '12px',
                  position: 'relative',
                  zIndex: 1
                }}>
                  <div style={{
                    width: '90px',
                    height: '90px',
                    background: 'var(--royal-navy)',
                    borderRadius: '12px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'var(--royal-gold)',
                    boxShadow: 'var(--shadow-md)'
                  }}>
                    {step.icon}
                  </div>
                  <div style={{
                    fontSize: '1.5rem',
                    fontWeight: '700',
                    color: 'var(--royal-gold)',
                    fontFamily: 'monospace'
                  }}>
                    {step.number}
                  </div>
                </div>

                {/* Content */}
                <div style={{
                  background: 'var(--bg-section-alt)',
                  padding: '32px',
                  borderRadius: '8px',
                  border: '1px solid var(--border-color)'
                }}>
                  <h3 style={{
                    fontSize: '1.5rem',
                    fontWeight: '600',
                    marginBottom: '16px',
                    color: 'var(--royal-navy)'
                  }}>
                    {step.title}
                  </h3>
                  <p style={{
                    fontSize: '1rem',
                    lineHeight: '1.7',
                    color: 'var(--text-secondary)',
                    marginBottom: '20px'
                  }}>
                    {step.description}
                  </p>
                  <ul style={{
                    listStyle: 'none',
                    padding: 0,
                    margin: 0,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '8px'
                  }}>
                    {step.details.map((detail, idx) => (
                      <li key={idx} style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        fontSize: '0.9rem',
                        color: 'var(--royal-navy)'
                      }}>
                        <div style={{
                          width: '6px',
                          height: '6px',
                          background: 'var(--royal-gold)',
                          borderRadius: '50%'
                        }}></div>
                        {detail}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Key Principles */}
      <section className="royal-section" style={{ background: 'var(--bg-section-alt)' }}>
        <div className="royal-container">
          <div style={{ textAlign: 'center', maxWidth: '700px', margin: '0 auto 60px' }}>
            <h2 className="royal-heading-lg">Our Service Principles</h2>
            <p className="royal-body-lg">
              What guides every job we take on.
            </p>
          </div>

          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
            gap: '32px'
          }}>
            {[
              {
                title: 'Clear Communication',
                description: 'Plain English. Regular updates. No jargon or confusion.'
              },
              {
                title: 'No Surprises',
                description: 'Transparent pricing. Quotes before work proceeds. Itemized invoices.'
              },
              {
                title: 'Quality First',
                description: 'Fix the cause, not just symptoms. Guaranteed workmanship.'
              },
              {
                title: 'Respect & Professionalism',
                description: 'Treat every property with care. Clean, tidy, professional service.'
              }
            ].map((principle, index) => (
              <div key={index} style={{
                padding: '32px',
                background: 'var(--royal-white)',
                borderRadius: '8px',
                border: '2px solid var(--border-color)',
                textAlign: 'center',
                transition: 'all 0.3s ease'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = 'var(--royal-gold)';
                e.currentTarget.style.transform = 'translateY(-4px)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = 'var(--border-color)';
                e.currentTarget.style.transform = 'translateY(0)';
              }}
              >
                <h3 style={{
                  fontSize: '1.15rem',
                  fontWeight: '600',
                  marginBottom: '12px',
                  color: 'var(--royal-navy)'
                }}>
                  {principle.title}
                </h3>
                <p className="royal-body">{principle.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section style={{
        background: 'linear-gradient(135deg, var(--royal-navy) 0%, #222222 100%)',
        padding: '80px 0',
        color: 'var(--royal-white)'
      }}>
        <div className="royal-container" style={{ textAlign: 'center' }}>
          <h2 style={{
            fontSize: 'clamp(2rem, 4vw, 3rem)',
            fontWeight: '700',
            marginBottom: '24px',
            color: 'var(--royal-white)'
          }}>
            Ready to Get Started?
          </h2>
          <p style={{
            fontSize: '1.25rem',
            marginBottom: '32px',
            color: 'rgba(255, 255, 255, 0.9)',
            maxWidth: '600px',
            margin: '0 auto 32px'
          }}>
            Contact us today to discuss your requirements or raise a job.
          </p>
          <div style={{ display: 'flex', gap: '16px', justifyContent: 'center', flexWrap: 'wrap' }}>
            <Link to="/contact" className="royal-btn-primary" style={{ background: 'var(--royal-gold)', color: 'var(--royal-navy)' }}>
              Contact Us
            </Link>
            <a href="tel:07710613783" className="royal-btn-light">
              Call 07710 613783
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};
